import _ from 'underscore';
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import getConfig from 'next/config';
import { movieTypePluralTitlesHash } from '@culture/utils/es5/movies';
import { movieTypesHash } from '@culture/utils/helpers/movies';
import { getAudioFileUrl } from '@culture/utils/helpers/files';
import { getEntityTags } from '@culture/common-helpers/tags';
import { getEntityUrl } from '@culture/common-helpers/cultureUtils';

import Container from 'components/Container';
import EntityCard from 'components/cards/EntityCard';
import GroupElements from 'components/GroupElements';
import Layout from 'components/Layout';
import Microdata from 'components/Microdata';
import MovieCard from 'components/cards/MovieCard';
import SectionLayout from 'components/SectionLayout';
import SlideSection from 'components/SlideSection';
import StyledContent from 'components/StyledContent';
import Tag from 'components/Tag';
import ViewsSenderContainer from 'containers/ViewsSender';

import CommonPropTypes from 'utils/propTypes/common';

import {
	getIsWasBroadcast,
	getMovieHasNationalProjectCultureTag
} from 'utils/helpers/movies';
import { PlaylistContainer } from '../../../components/Audio/PlaylistContainer';
import ConcertProgram from './components/ConcertProgram';
import Gallery from './components/Gallery';
import MoviePreview from './components/Preview';
import Participants from './components/Participants';
import BroadcastInformation from './components/BroadcastInformation';

const { publicRuntimeConfig } = getConfig();

const Movie = ({
	movie,
	microdata,
	breadcrumbs,
	rubric,
	publications,
	recommendations,
	materials
}) => {
	const recommendationPath = rubric ? rubric.path.split('/', 2).join('/') : '';
	const tags = getEntityTags(movie);

	const materialsList = useMemo(
		() =>
			movie?.materials
				.map((material) => {
					if (material.type === 'audio') {
						return {
							id: material?._id,
							title: material?.title,
							src: getAudioFileUrl(material?.files[0], {
								config: publicRuntimeConfig
							})
						};
					}
					return '';
				})
				.filter(Boolean),
		[movie]
	);
	const isMovieWasBroadcast = getIsWasBroadcast(movie);
	const hasNationalProjectCultureTag = getMovieHasNationalProjectCultureTag(
		movie.tags
	);
	const showDisclaimer = isMovieWasBroadcast && hasNationalProjectCultureTag;
	return (
		<>
			<Layout
				meta={movie.meta}
				entity={movie}
				entityType="movies"
				rubricPath={rubric?.path}
			>
				<SectionLayout gutter="bottom">
					<MoviePreview
						key={movie._id}
						data={movie}
						breadcrumbs={breadcrumbs}
						theme="entity"
					/>
					{showDisclaimer ? <BroadcastInformation broadcast={movie} /> : null}
				</SectionLayout>
				<SectionLayout theme="light" blockGutter="compact" gutter="small">
					<Container width="narrow">
						<StyledContent blocks={movie.jsonText} spacing="large" />
						{!!materialsList.length && (
							<PlaylistContainer trackList={materialsList} />
						)}
						<Participants data={movie.participants} movieType={movie.type} />
						{movie.type === movieTypesHash.concert &&
							(movie.shortText || movie.jsonAnnotation) && (
								<ConcertProgram
									shortText={movie.shortText}
									annotation={movie.jsonAnnotation}
								/>
							)}
					</Container>
					{!_(materials).isEmpty() && <Gallery materials={materials} />}
					{!!tags.length && (
						<Container width="narrow">
							<GroupElements>
								{tags.map((tag) => (
									<Tag
										key={tag.url}
										title={tag.title}
										url={tag.url}
										textTheme="light"
										theme="bordered"
									/>
								))}
							</GroupElements>
						</Container>
					)}
				</SectionLayout>
				{!!(publications.length + recommendations.length) && (
					<SectionLayout gutter="largeFull">
						{!_.isEmpty(publications) && (
							<SlideSection title="В подборках" items={publications}>
								{publications.map((publication) => (
									<EntityCard
										key={publication._id}
										title={publication.title}
										image={publication.thumbnailFile}
										url={getEntityUrl('publications', publication)}
									/>
								))}
							</SlideSection>
						)}
						{!_.isEmpty(recommendations) && (
							<SlideSection
								title="Смотрите также"
								items={recommendations}
								moreControl={{
									title: `Все ${movieTypePluralTitlesHash[
										movie.type
									].toLowerCase()}`,
									as: `/${recommendationPath}`,
									url: `/${recommendationPath}/[[...pathParams]]`
								}}
							>
								{recommendations.map((entity) => (
									<MovieCard key={entity._id} data={entity} />
								))}
							</SlideSection>
						)}
					</SectionLayout>
				)}
			</Layout>
			<ViewsSenderContainer entityType="movies" entityId={movie._id} />
			{microdata && <Microdata>{microdata}</Microdata>}
		</>
	);
};

Movie.propTypes = {
	movie: CommonPropTypes.entity.isRequired,
	microdata: PropTypes.string,
	breadcrumbs: CommonPropTypes.breadcrumbs.isRequired,
	rubric: CommonPropTypes.rubric.isRequired,
	publications: CommonPropTypes.entities.isRequired,
	recommendations: CommonPropTypes.entities.isRequired,
	materials: PropTypes.arrayOf(CommonPropTypes.file)
};

export default Movie;
